import { useRef, useCallback } from "react";
import { TaskStatus } from "@/types";

/**
 * Manages the three refetch functions for the Kanban columns (PENDING,
 * IN_PROGRESS, COMPLETED). The page passes refetchRef to each KanbanColumn,
 * which populates it with the column's refetch callback. The page can then
 * call refetchColumn(status) or refetchAll() to trigger refreshes after
 * mutations or subscription events.
 */
export function useColumnRefetch() {
  const pendingRefetchRef = useRef<(() => void) | null>(null);
  const inProgressRefetchRef = useRef<(() => void) | null>(null);
  const completedRefetchRef = useRef<(() => void) | null>(null);

  const refetchAll = useCallback(() => {
    pendingRefetchRef.current?.();
    inProgressRefetchRef.current?.();
    completedRefetchRef.current?.();
  }, []);

  const refetchColumn = useCallback((status: TaskStatus) => {
    switch (status) {
      case TaskStatus.PENDING:
        pendingRefetchRef.current?.();
        break;
      case TaskStatus.IN_PROGRESS:
      case TaskStatus.PROBLEM:
        inProgressRefetchRef.current?.();
        break;
      case TaskStatus.COMPLETED:
        completedRefetchRef.current?.();
        break;
    }
  }, []);

  return {
    refs: {
      pending: pendingRefetchRef,
      inProgress: inProgressRefetchRef,
      completed: completedRefetchRef,
    },
    refetchAll,
    refetchColumn,
  };
}
